import React, { useState, useEffect } from 'react';
import "../Styling/CEODashboard.css";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from '../Context/AuthContext';

const dummyData = {
  ap: {
    accountBalances: [
      { bank: 'HDFC Bank', balance: 2500000, currency: 'INR' },
      { bank: 'ICICI Bank', balance: 1800000, currency: 'INR' },
      { bank: 'SBI', balance: 3200000, currency: 'INR' },
      { bank: 'Axis Bank', balance: 950000, currency: 'INR' }
    ],
    overduePayments: [
      { id: 'INV-AP-2401', vendor: 'Tech Solutions Ltd', amount: 145000, overdueDate: '2024-11-15' },
      { id: 'INV-AP-2398', vendor: 'Office Supplies Co', amount: 32000, overdueDate: '2024-11-20' },
      { id: 'INV-AP-2385', vendor: 'Consulting Partners', amount: 280000, overdueDate: '2024-11-05' }
    ],
    upcomingPayments: {
      thisMonth: 1850000,
      nextMonth: 2100000,
      future: 3500000
    }
  },
  
  ar: {
    accountBalances: [
      { account: 'Trade Receivables', balance: 5600000, currency: 'INR' },
      { account: 'Security Deposits', balance: 450000, currency: 'INR' },
      { account: 'Advances Recoverable', balance: 280000, currency: 'INR' }
    ],
    overdueReceivables: [
      { id: 'INV-AR-3201', customer: 'Global Enterprises', amount: 520000, overdueDate: '2024-11-10' },
      { id: 'INV-AR-3189', customer: 'Metro Industries', amount: 185000, overdueDate: '2024-11-18' },
      { id: 'INV-AR-3175', customer: 'Sunrise Corporation', amount: 340000, overdueDate: '2024-11-08' }
    ],
    upcomingReceivables: {
      thisMonth: 3200000,
      nextMonth: 2800000,
      future: 4500000
    }
  }
};

// API Functions
const fetchTodayAvailability = async (filters, token) => {
  if (!filters.companyCode) {
    throw new Error("CompanyCode missing");
  }

  const response = await axios.get(
    `${Base_URL}/CeoDashboard/TodayAvailablity`,
    {
      params: {
        org_id: parseInt(filters.orgId, 10),
        CompanyCode: parseInt(filters.companyCode, 10),
        postingDate: filters.postingDate
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  return response.data;
};

// WFH Today API
const fetchWFHEmployees = async (filters, token) => {
  try {
    const response = await axios.get(
      `${Base_URL}/CeoDashboard/wfh-today`,
      {
        params: {
          org_id: parseInt(filters.orgId, 10),
          companyCode: parseInt(filters.companyCode, 10)
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching WFH employees:", error);
    return [];
  }
};

// Today's Leave API
const fetchTodaysLeaveEmployees = async (filters, token) => {
  try {
    const response = await axios.get(
      `${Base_URL}/CeoDashboard/todays-leave`,
      {
        params: {
          org_id: parseInt(filters.orgId, 10),
          companyCode: parseInt(filters.companyCode, 10)
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching today's leave employees:", error);
    return [];
  }
};

const fetchIncomingLeaveRequests = async (filters, user, token) => {
  try {
    const response = await axios.get(
      `${Base_URL}/CeoDashboard/incoming-leave-request`,
      {
        params: {
          org: user?.organisationId,
          companycode: parseInt(filters.companyCode, 10),
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error fetching incoming leave requests:", error);
    return [];
  }
};

// API function for Monthly Leaves Combined
const fetchMonthlyLeavesCombined = async (filters, user, token) => {
  try {
    const response = await axios.get(
      `${Base_URL}/CeoDashboard/monthly-leaves/combined`,
      {
        params: {
          org_id: user?.organisationId || filters.orgId,
          CompanyCode: filters.companyCode || null,
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching monthly leaves combined:", error);
    return {
      totalCurrentMonth: 0,
      totalNextMonth: 0,
      leaves: []
    };
  }
};

const CEODashboard = () => {
  const { user, token } = useAuth();

  const [filters, setFilters] = useState({
    orgId: "",
    companyCode: "",
    postingDate: new Date().toISOString().split("T")[0]
  });

  const [companyCodes, setCompanyCodes] = useState([]);
  
  // HR Data states
  const [availabilityData, setAvailabilityData] = useState(null);
  const [wfhEmployees, setWFHEmployees] = useState([]);
  const [leaveEmployees, setLeaveEmployees] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [monthlyLeavesData, setMonthlyLeavesData] = useState({
    totalCurrentMonth: 0,
    totalNextMonth: 0,
    leaves: []
  });
  
  // State to control visibility
  const [showAllLeaveRequests, setShowAllLeaveRequests] = useState(false);
  const [showMonthlyLeavesTable, setShowMonthlyLeavesTable] = useState(false);
  const [showWFHEmployeesTable, setShowWFHEmployeesTable] = useState(false);
  const [showLeaveEmployeesTable, setShowLeaveEmployeesTable] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCompanyCodes = async () => {
      if (!user?.organisationId) return;

      try {
        const url = `${Base_URL}/Balancesheet/bs-company-codes-with-names/${user.organisationId}`;

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const fetchedCodes = response.data || [];
        setCompanyCodes(fetchedCodes);

        // AUTO SELECT FIRST COMPANY
        if (fetchedCodes.length > 0) {
          setFilters(prev => ({
            ...prev,
            orgId: user.organisationId,
            companyCode: fetchedCodes[0].companyCode.toString(),
          }));
        }
      } catch (error) {
        console.error("Error fetching company codes:", error);
        setCompanyCodes([]);
      }
    };

    fetchCompanyCodes();
  }, [user, token]);

  useEffect(() => {
    const fetchHRData = async () => {
      if (!filters.orgId || !filters.companyCode) return;

      setLoading(true);

      try {
        // 1️⃣ Availability
        const availabilityRes = await fetchTodayAvailability(
          {
            ...filters,
            companyCode: parseInt(filters.companyCode, 10),
          },
          token
        );
        setAvailabilityData(availabilityRes);

        // 2️⃣ WFH Employees
        const wfhRes = await fetchWFHEmployees(filters, token);
        setWFHEmployees(wfhRes);

        // 3️⃣ Today's Leave Employees
        const leaveRes = await fetchTodaysLeaveEmployees(filters, token);
        setLeaveEmployees(leaveRes);

        // 4️⃣ Incoming Leave Requests
        const incomingRes = await fetchIncomingLeaveRequests(filters, user, token);
        setIncomingRequests(incomingRes);

        // 5️⃣ Monthly Leaves Combined
        const monthlyLeavesRes = await fetchMonthlyLeavesCombined(filters, user, token);
        setMonthlyLeavesData(monthlyLeavesRes);

      } catch (err) {
        console.error("HR Dashboard Error:", err);
        setIncomingRequests([]);
        setWFHEmployees([]);
        setLeaveEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHRData();
  }, [filters.orgId, filters.companyCode, filters.postingDate, user, token]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Toggle functions
  const toggleAllLeaveRequests = () => {
    setShowAllLeaveRequests(!showAllLeaveRequests);
  };

  const toggleMonthlyLeavesTable = (monthType = 'all') => {
    setSelectedMonth(monthType);
    setShowMonthlyLeavesTable(!showMonthlyLeavesTable);
  };

  const toggleWFHEmployeesTable = () => {
    setShowWFHEmployeesTable(!showWFHEmployeesTable);
  };

  const toggleLeaveEmployeesTable = () => {
    setShowLeaveEmployeesTable(!showLeaveEmployeesTable);
  };

  // Filter leaves by month category
  const getFilteredLeaves = () => {
    if (selectedMonth === 'current') {
      return monthlyLeavesData.leaves.filter(leave => leave.monthCategory === 'CurrentMonth');
    } else if (selectedMonth === 'next') {
      return monthlyLeavesData.leaves.filter(leave => leave.monthCategory === 'NextMonth');
    }
    return monthlyLeavesData.leaves;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Utility function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Small Metric Card Component
  const SmallMetricCard = ({ title, value, bgColor = "ceo-metric-card-blue" }) => (
    <div className={`ceo-metric-card ${bgColor}`}>
      <p className="ceo-metric-card-title">{title}</p>
      <p className="ceo-metric-card-value">{value}</p>
    </div>
  );

  // Get first 3 requests/employees for display
  const displayedRequests = incomingRequests.slice(0, 3);
  const displayedWFHEmployees = wfhEmployees.slice(0, 3);
  const displayedLeaveEmployees = leaveEmployees.slice(0, 3);
  
  const remainingRequestsCount = Math.max(0, incomingRequests.length - 3);
  const remainingWFHCount = Math.max(0, wfhEmployees.length - 3);
  const remainingLeaveCount = Math.max(0, leaveEmployees.length - 3);

  return (
    <div className="ceo-dashboard-container">
      {/* Header */}
      <h1 className="ceo-dashboard-title">
        CEO Financial & HR Dashboard
      </h1>

      {/* Global Filters */}
      <div className="ceo-filters-container">
        <div className="ceo-filters-wrapper">
          <input
            type="text"
            placeholder="Organisation ID"
            value={filters.orgId}
            onChange={(e) => handleFilterChange('orgId', e.target.value)}
            className="ceo-filter-input"
            disabled={!!user?.organisationId}
          />

          <select
            value={filters.companyCode}
            onChange={(e) => handleFilterChange('companyCode', e.target.value)}
            className="ceo-filter-select"
            disabled={!filters.orgId || loading}
          >
            <option value="">Select Company</option>
            {companyCodes.map((company) => (
              <option key={company.code} value={company.code}>
                {company.name} ({company.code})
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filters.postingDate}
            onChange={(e) => handleFilterChange('postingDate', e.target.value)}
            className="ceo-filter-date"
          />
        </div>
        {loading && (
          <div className="ceo-loading-indicator">
            Loading data...
          </div>
        )}
      </div>

      <div className="ceo-main-grid">
        {/* ACCOUNT PAYABLE SECTION */}
        <div className="ceo-section-card">
          <div className="ceo-section-header">
            <span className="ceo-section-icon">💰</span>
            <h2 className="ceo-section-title ceo-section-title-blue">Account Payable</h2>
          </div>
          <div className="ceo-subsection">
            <h3 className="ceo-subsection-title">ACCOUNT BALANCES</h3>
            <div className="ceo-accounts-grid">
              {dummyData.ap.accountBalances.map((account, idx) => (
                <SmallMetricCard
                  key={idx}
                  title={account.bank}
                  value={formatCurrency(account.balance)}
                  bgColor="ceo-metric-card-blue"
                />
              ))}
            </div>
          </div>
          <div className="ceo-subsection">
            <h3 className="ceo-subsection-title">OVERDUE PAYMENTS</h3>
            <div className="ceo-alert-card ceo-alert-card-red">
              <div className="ceo-alert-card-content">
                {dummyData.ap.overduePayments.map((payment) => (
                  <div key={payment.id} className="ceo-alert-item">
                    <div className="ceo-alert-item-info">
                      <p className="ceo-alert-item-id">{payment.id}</p>
                      <p className="ceo-alert-item-subtext">{payment.vendor}</p>
                      <span className="ceo-alert-date">
                        {payment.overdueDate}
                      </span>
                    </div>
                    <p className="ceo-alert-amount">
                      {formatCurrency(payment.amount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h3 className="ceo-subsection-title">UPCOMING PAYMENTS</h3>
            <div className="ceo-gradient-card ceo-gradient-card-blue">
              <div className="ceo-gradient-item">
                <span className="ceo-gradient-item-label">This Month</span>
                <span className="ceo-gradient-item-value ceo-gradient-blue-primary">
                  {formatCurrency(dummyData.ap.upcomingPayments.thisMonth)}
                </span>
              </div>
              <div className="ceo-gradient-item">
                <span className="ceo-gradient-item-label">Next Month</span>
                <span className="ceo-gradient-item-value ceo-gradient-blue-secondary">
                  {formatCurrency(dummyData.ap.upcomingPayments.nextMonth)}
                </span>
              </div>
              <div className="ceo-gradient-item">
                <span className="ceo-gradient-item-label">Future</span>
                <span className="ceo-gradient-item-value ceo-gradient-blue-tertiary">
                  {formatCurrency(dummyData.ap.upcomingPayments.future)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ACCOUNT RECEIVABLE SECTION */}
        <div className="ceo-section-card">
          <div className="ceo-section-header">
            <span className="ceo-section-icon">💸</span>
            <h2 className="ceo-section-title ceo-section-title-green">Account Receivable</h2>
          </div>
          <div className="ceo-subsection">
            <h3 className="ceo-subsection-title">ACCOUNT BALANCES</h3>
            <div className="ceo-accounts-grid-single">
              {dummyData.ar.accountBalances.map((account, idx) => (
                <SmallMetricCard
                  key={idx}
                  title={account.account}
                  value={formatCurrency(account.balance)}
                  bgColor="ceo-metric-card-green"
                />
              ))}
            </div>
          </div>
          <div className="ceo-subsection">
            <h3 className="ceo-subsection-title">OVERDUE RECEIVABLES</h3>
            <div className="ceo-alert-card ceo-alert-card-red">
              <div className="ceo-alert-card-content">
                {dummyData.ar.overdueReceivables.map((receivable) => (
                  <div key={receivable.id} className="ceo-alert-item">
                    <div className="ceo-alert-item-info">
                      <p className="ceo-alert-item-id">{receivable.id}</p>
                      <p className="ceo-alert-item-subtext">{receivable.customer}</p>
                      <span className="ceo-alert-date">
                        {receivable.overdueDate}
                      </span>
                    </div>
                    <p className="ceo-alert-amount">
                      {formatCurrency(receivable.amount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h3 className="ceo-subsection-title">UPCOMING RECEIVABLES</h3>
            <div className="ceo-gradient-card ceo-gradient-card-green">
              <div className="ceo-gradient-item">
                <span className="ceo-gradient-item-label">This Month</span>
                <span className="ceo-gradient-item-value ceo-gradient-green-primary">
                  {formatCurrency(dummyData.ar.upcomingReceivables.thisMonth)}
                </span>
              </div>
              <div className="ceo-gradient-item">
                <span className="ceo-gradient-item-label">Next Month</span>
                <span className="ceo-gradient-item-value ceo-gradient-green-secondary">
                  {formatCurrency(dummyData.ar.upcomingReceivables.nextMonth)}
                </span>
              </div>
              <div className="ceo-gradient-item">
                <span className="ceo-gradient-item-label">Future</span>
                <span className="ceo-gradient-item-value ceo-gradient-green-tertiary">
                  {formatCurrency(dummyData.ar.upcomingReceivables.future)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* HR SECTION */}
        <div className="ceo-section-card">
          <div className="ceo-section-header">
            <span className="ceo-section-icon">🧑‍🤝‍🧑</span>
            <h2 className="ceo-section-title ceo-section-title-purple">
              Employee Availability
              {loading && <span className="ceo-loading-badge">Loading...</span>}
            </h2>
          </div>

          {/* Availability Cards */}
          <div className="ceo-subsection">
            <h3 className="ceo-subsection-title">AVAILABILITY</h3>
            <div className="ceo-accounts-grid">
              {availabilityData ? (
                <>
                  <SmallMetricCard
                    title="Total Employees"
                    value={availabilityData.total || 0}
                    bgColor="ceo-metric-card-indigo"
                  />
                  <SmallMetricCard
                    title="Present"
                    value={availabilityData.present || 0}
                    bgColor="ceo-metric-card-green"
                  />
                  
                  {/* WFH Card with employee names */}
                  <div 
                    className="ceo-metric-card ceo-metric-card-teal clickable"
                    onClick={toggleWFHEmployeesTable}
                    style={{ cursor: 'pointer' }}
                  >
                    <p className="ceo-metric-card-title">WFH</p>
                    <p className="ceo-metric-card-value">{availabilityData.wfh || 0}</p>
                    
                    {/* Show employee names if available */}
                    {displayedWFHEmployees.length > 0 && (
                      <div className="ceo-employee-names">
                        {displayedWFHEmployees.map((emp, idx) => (
                          <div key={idx} className="ceo-employee-name-item">
                            <small>{emp.name || 'Unknown'}</small>
                          </div>
                        ))}
                        {remainingWFHCount > 0 && (
                          <div className="ceo-view-more-small">
                            <small>+{remainingWFHCount} more</small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* On Leave Card with employee names */}
                  <div 
                    className="ceo-metric-card ceo-metric-card-orange clickable"
                    onClick={toggleLeaveEmployeesTable}
                    style={{ cursor: 'pointer' }}
                  >
                    <p className="ceo-metric-card-title">On Leave</p>
                    <p className="ceo-metric-card-value">{availabilityData.onLeave || 0}</p>
                    
                    {/* Show employee names if available */}
                    {displayedLeaveEmployees.length > 0 && (
                      <div className="ceo-employee-names">
                        {displayedLeaveEmployees.map((emp, idx) => (
                          <div key={idx} className="ceo-employee-name-item">
                            <small>{emp.employeeName || 'Unknown'}</small>
                            <small className="ceo-leave-type-tag">{emp.leaveType || ''}</small>
                          </div>
                        ))}
                        {remainingLeaveCount > 0 && (
                          <div className="ceo-view-more-small">
                            <small>+{remainingLeaveCount} more</small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <SmallMetricCard
                    title="Total Present"
                    value="Loading..."
                    bgColor="ceo-metric-card-indigo"
                  />
                  <div className="ceo-metric-card ceo-metric-card-teal">
                    <p className="ceo-metric-card-title">WFH</p>
                    <p className="ceo-metric-card-value">Loading...</p>
                  </div>
                  <div className="ceo-metric-card ceo-metric-card-orange">
                    <p className="ceo-metric-card-title">On Leave</p>
                    <p className="ceo-metric-card-value">Loading...</p>
                  </div>
                </>
              )}
            </div>
            {availabilityData && (
              <div className="ceo-total-info">
                <small>Total Employees: {availabilityData.total || 0}</small>
                <small>Date: {filters.postingDate}</small>
              </div>
            )}
          </div>

          {/* Incoming Leave Requests - Show only 3 */}
          <div className="ceo-subsection">
            <h3 className="ceo-subsection-title">INCOMING LEAVE REQUESTS</h3>
            <div className="ceo-alert-card ceo-alert-card-purple">
              <div className="ceo-alert-card-content">
                {displayedRequests.length > 0 ? (
                  displayedRequests.map((request, idx) => (
                    <div 
                      key={idx} 
                      className="ceo-leave-request-item"
                    >
                      <div className="ceo-leave-request-header">
                        <p className="ceo-leave-request-name">
                          {request.employeename || request.employee || 'Unknown Employee'}
                        </p>
                        <span className={`ceo-leave-request-status ceo-status-${request.status?.toLowerCase() || 'pending'}`}>
                          {request.status || 'Pending'}
                        </span>
                      </div>
                      <p className="ceo-leave-request-type">
                        {request.leaveType || 'Not Specified'}
                      </p>
                      <p className="ceo-leave-request-date">
                        {formatDate(request.startdate)}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="ceo-no-data">
                    {loading ? "Loading..." : "No incoming leave requests"}
                  </div>
                )}
                {remainingRequestsCount > 0 && (
                  <div 
                    className="ceo-view-more clickable"
                    onClick={toggleAllLeaveRequests}
                    style={{ cursor: 'pointer' }}
                  >
                    <small>+{remainingRequestsCount} more requests</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Monthly Leave Totals - Clickable Cards */}
          <div>
            <h3 className="ceo-subsection-title">MONTHLY LEAVE TOTALS</h3>
            <div className="ceo-gradient-card ceo-gradient-card-purple">
              <div 
                className="ceo-monthly-total-item clickable"
                onClick={() => toggleMonthlyLeavesTable('current')}
                style={{ cursor: 'pointer' }}
              >
                <p className="ceo-monthly-total-label">This Month</p>
                <p className="ceo-monthly-total-value ceo-monthly-total-purple">
                  {monthlyLeavesData.totalCurrentMonth || 0}
                </p>
                <p className="ceo-monthly-total-subtext">leaves scheduled</p>
              </div>
              
              <div 
                className="ceo-monthly-total-item clickable"
                onClick={() => toggleMonthlyLeavesTable('next')}
                style={{ cursor: 'pointer' }}
              >
                <p className="ceo-monthly-total-label">Next Month</p>
                <p className="ceo-monthly-total-value ceo-monthly-total-pink">
                  {monthlyLeavesData.totalNextMonth || 0}
                </p>
                <p className="ceo-monthly-total-subtext">leaves scheduled</p>
              </div>
              
              <div 
                className="ceo-view-all-container"
                onClick={() => toggleMonthlyLeavesTable('all')}
                style={{ cursor: 'pointer' }}
              >
                <small>View All Monthly Leaves →</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Leave Requests Table */}
      {showAllLeaveRequests && (
        <div className="ceo-all-requests-container">
          <div className="ceo-all-requests-header">
            <h3>All Incoming Leave Requests ({incomingRequests.length})</h3>
            <button 
              className="ceo-close-btn"
              onClick={toggleAllLeaveRequests}
            >
              ✕ Close
            </button>
          </div>
          
          <div className="ceo-requests-table-container">
            <table className="ceo-requests-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Leave Type</th>
                  <th>Duration</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {incomingRequests.length > 0 ? (
                  incomingRequests.map((request, index) => (
                    <tr key={index}>
                      <td>{request.employeename || request.employee || 'Unknown'}</td>
                      <td>{request.leaveType || 'Not Specified'}</td>
                      <td>{request.duration || 1} day(s)</td>
                      <td>{formatDate(request.startdate)}</td>
                      <td>{formatDate(request.endDate)}</td>
                      <td>
                        <span className={`ceo-table-status ceo-status-${request.status?.toLowerCase() || 'pending'}`}>
                          {request.status || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      No leave requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Leaves Table */}
      {showMonthlyLeavesTable && (
        <div className="ceo-monthly-leaves-container">
          <div className="ceo-monthly-leaves-header">
            <h3>
              Monthly Leaves Details 
              {selectedMonth === 'current' ? ' - Current Month' : 
               selectedMonth === 'next' ? ' - Next Month' : 
               ' - All Months'}
              <span className="ceo-leaves-count">
                ({getFilteredLeaves().length} leaves)
              </span>
            </h3>
            <div className="ceo-monthly-leaves-actions">
              <div className="ceo-month-filter-buttons">
                <button 
                  className={`ceo-filter-btn ${selectedMonth === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedMonth('all')}
                >
                  All
                </button>
                <button 
                  className={`ceo-filter-btn ${selectedMonth === 'current' ? 'active' : ''}`}
                  onClick={() => setSelectedMonth('current')}
                >
                  Current Month ({monthlyLeavesData.totalCurrentMonth || 0})
                </button>
                <button 
                  className={`ceo-filter-btn ${selectedMonth === 'next' ? 'active' : ''}`}
                  onClick={() => setSelectedMonth('next')}
                >
                  Next Month ({monthlyLeavesData.totalNextMonth || 0})
                </button>
              </div>
              <button 
                className="ceo-close-btn"
                onClick={() => setShowMonthlyLeavesTable(false)}
              >
                ✕ Close
              </button>
            </div>
          </div>
          
          <div className="ceo-monthly-table-container">
            <table className="ceo-monthly-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Leave Type</th>
                  <th>Leave From</th>
                  <th>Leave To</th>
                  <th>Month Category</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredLeaves().length > 0 ? (
                  getFilteredLeaves().map((leave, index) => (
                    <tr key={index}>
                      <td>{leave.employeeName || 'Unknown'}</td>
                      <td>{leave.leaveType || 'Not Specified'}</td>
                      <td>{formatDate(leave.leaveFrom)}</td>
                      <td>{formatDate(leave.leaveTo)}</td>
                      <td>
                        <span className={`ceo-month-badge ceo-month-${leave.monthCategory?.toLowerCase() || 'current'}`}>
                          {leave.monthCategory || 'Current Month'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                      No leaves found for the selected month
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* WFH Employees Table */}
      {showWFHEmployeesTable && (
        <div className="ceo-employees-container ceo-wfh-container">
          <div className="ceo-employees-header">
            <h3>
              Work From Home Employees Today ({wfhEmployees.length})
              <span className="ceo-employees-count">
                {availabilityData?.wfh || 0} employees
              </span>
            </h3>
            <button 
              className="ceo-close-btn"
              onClick={toggleWFHEmployeesTable}
            >
              ✕ Close
            </button>
          </div>
          
          <div className="ceo-employees-table-container">
            <table className="ceo-employees-table">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>WFH Start Date</th>
                  <th>WFH End Date</th>
                </tr>
              </thead>
              <tbody>
                {wfhEmployees.length > 0 ? (
                  wfhEmployees.map((employee, index) => (
                    <tr key={index}>
                      <td>{employee.resourceId || 'N/A'}</td>
                      <td>{employee.name || 'Unknown'}</td>
                      <td>{formatDate(employee.startDate)}</td>
                      <td>{formatDate(employee.endDate)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                      {loading ? 'Loading...' : 'No employees working from home today'}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" style={{ textAlign: 'right', padding: '10px', fontWeight: 'bold' }}>
                    Total WFH Employees: {wfhEmployees.length}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Leave Employees Table */}
      {showLeaveEmployeesTable && (
        <div className="ceo-employees-container ceo-leave-container">
          <div className="ceo-employees-header">
            <h3>
              Employees on Leave Today ({leaveEmployees.length})
              <span className="ceo-employees-count">
                {availabilityData?.onLeave || 0} employees
              </span>
            </h3>
            <button 
              className="ceo-close-btn"
              onClick={toggleLeaveEmployeesTable}
            >
              ✕ Close
            </button>
          </div>
          
          <div className="ceo-employees-table-container">
            <table className="ceo-employees-table">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>Leave Type</th>
                  <th>Leave Start Date</th>
                  <th>Leave End Date</th>
                </tr>
              </thead>
              <tbody>
                {leaveEmployees.length > 0 ? (
                  leaveEmployees.map((employee, index) => (
                    <tr key={index}>
                      <td>{employee.mav_UserID || 'N/A'}</td>
                      <td>{employee.employeeName || 'Unknown'}</td>
                      <td>{employee.leaveType || 'Not Specified'}</td>
                      <td>{formatDate(employee.leaveDate)}</td>
                      <td>{formatDate(employee.endLeaveDate)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                      {loading ? 'Loading...' : 'No employees on leave today'}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5" style={{ textAlign: 'right', padding: '10px', fontWeight: 'bold' }}>
                    Total Employees on Leave: {leaveEmployees.length}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CEODashboard;